// packages/web-ui/src/panels/views/ViewRenderer.tsx
//
// W4-S1 — React Flow renderer lifted from
// `tools/view-renderer/reactflow-app/src/App.jsx`. Implements the Arch §9.2
// component contract verbatim (props + internal pipeline a–e).
//
// Dependency-ordering note: this story can scaffold ahead of W2-S4. The
// component is self-contained (no dependency on TabContentArea internals);
// W2-S4 wires `<TabContent>` → `<ViewRenderer>` via the exported
// `ViewRendererHandle` ref API.

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ForwardedRef,
  type JSX,
} from 'react';
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  reconnectEdge,
  useNodesInitialized,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type {
  ReactFlowLayoutJson,
  LayoutIntent,
} from '@tessa-lean/base/api-schemas/index.js';
import { resolveHiddenNodeIds, defaultViewScope } from '@tessa-lean/base';
import { createApiContext, type ApiContext } from '../../api/client.js';
import { viewsApi as defaultViewsApi } from '../../api/clients/views.js';
import { PartNode } from './PartNode.js';
import { PortNode } from './PortNode.js';
import { InterfaceNode } from './InterfaceNode.js';
import { ConnectionEdge } from './ConnectionEdge.js';
import { EdgeLegend } from './EdgeLegend.js';
import {
  buildReactFlowGraph,
  needsFirstRenderLayout,
  VIRTUALISE_THRESHOLD,
} from './layout-builder.js';
import { runLayoutWithIntent } from './layout-with-intent.js';
import { injectThemeCss } from './theme-css.js';
import './view-renderer.css';

// ---- Public types -----------------------------------------------------------

export interface ViewRendererProps {
  viewId: string;
  /** When true (default), the renderer fetches layout JSON via the API. */
  fetchLayout?: boolean;
  /** When provided, overrides the layout fetch with an explicit value (for previews / fixtures). */
  layoutOverride?: ReactFlowLayoutJson;
  /** Read-only mode disables drag and edit affordances. */
  readOnly?: boolean;
  /** Called when the user explicitly saves; receives the latest layout. */
  onLayoutSave?: (layout: ReactFlowLayoutJson) => void;
  /** Theme override (otherwise inherits from <ThemeProvider>). */
  theme?: 'light' | 'dark' | 'auto';
  /** Override API context (test-only). */
  apiContext?: ApiContext;
  /** Override viewsApi (test-only). */
  viewsApi?: Pick<typeof defaultViewsApi, 'getLayout' | 'clearScope'>;
  /**
   * Opt-in zoom + pan affordances. When true, the renderer enables
   * `panOnDrag`, wheel/pinch/double-click zoom (`minZoom` 0.1, `maxZoom` 4)
   * and mounts the React Flow `<Controls>` UI (zoom in / out / fit-view).
   * Default `false` preserves the Views-tab's fixed-viewport behaviour;
   * the inline expand-modal lightbox flips this on so users can zoom
   * large composition trees without leaving chat.
   */
  enableZoom?: boolean;
  /**
   * When true, wheel-zoom requires holding Ctrl and wheel-pan requires
   * holding Shift (CAD / Figma convention). Used by the Views-tab panel
   * so that a plain wheel event doesn't hijack scrolling. The expand-modal
   * keeps the simpler "wheel = zoom" default.
   */
  requireZoomModifier?: boolean;
  /**
   * Position for the in-canvas React Flow `<Controls>` UI (zoom in/out/fit).
   * Defaults to React Flow's `'bottom-left'`. The Views-tab panel overrides
   * to `'bottom-right'` to avoid overlapping the bottom-left legend.
   */
  controlsPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /**
   * Bump to force a re-fetch of the persisted layout WITHOUT changing `viewId`.
   * The Views-tab panel increments this after a successful in-place relayout
   * (`POST /views/:viewId/relayout` persisted a new `layoutIntent`) so the
   * renderer re-reads `layout.json` and re-applies ELK with the new intent.
   * A no-op for the default (undefined) value.
   */
  reloadNonce?: number;
}

export interface ViewRendererHandle {
  /** Current layout JSON, including any drag/edit deltas. */
  getLayout(): ReactFlowLayoutJson;
  /** True if the user has dragged or edited a label since mount/last save. */
  isDirty(): boolean;
  /** Re-runs elkjs auto-layout. Honours `prefers-reduced-motion`. */
  resetLayout(): void;
  /** Calls `onLayoutSave(getLayout())` and clears the dirty flag. */
  save(): void;
  /** Fits the current view to all nodes. */
  fitView(): void;
  /** Step zoom in (multiplicative; React Flow default factor). */
  zoomIn(): void;
  /** Step zoom out (multiplicative; React Flow default factor). */
  zoomOut(): void;
  /** Returns the node + edge counts for the aria-describedby summary region. */
  getCounts(): { nodes: number; edges: number };
  /** Current viewport zoom (1.0 = identity). Reads from the React Flow store. */
  getZoom(): number;
}

// ---- Internal helpers -------------------------------------------------------

const NODE_TYPES = {
  part: PartNode,
  port: PortNode,
  interface: InterfaceNode,
};
const EDGE_TYPES = {
  connection: ConnectionEdge,
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; layout: ReactFlowLayoutJson; nodes: Node[]; edges: Edge[] };

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function nodesToLayoutPositions(
  nodes: ReadonlyArray<Node>,
): Record<string, { x: number; y: number }> {
  const positions: Record<string, { x: number; y: number }> = {};
  for (const n of nodes) {
    positions[n.id] = { x: n.position.x, y: n.position.y };
  }
  return positions;
}

/**
 * Read the optional `layoutIntent` off a persisted layout (LV1-S5 persists it;
 * until then it is absent and this returns `undefined` ⇒ the no-intent path).
 * Defensive `as` cast because `ReactFlowLayoutJson` does not yet declare the
 * field — kept a single-site read so LV1-S5 can drop the cast cleanly.
 */
function layoutIntentOf(layout: ReactFlowLayoutJson): LayoutIntent | undefined {
  const candidate = (layout as { layoutIntent?: LayoutIntent }).layoutIntent;
  return candidate ?? undefined;
}

/**
 * DLA back-compat — a `layout.json` written before the `deemphasis` field carries
 * an 8-field `layoutIntent`. The strict `PUT /views/:id/layout` schema now
 * requires the field, so normalise a persisted intent to the current shape
 * before a manual save (fill the new array as empty — byte-identical to how such
 * a legacy view renders).
 */
function normalizeIntentForSave(intent: LayoutIntent): LayoutIntent {
  return { ...intent, deemphasis: Array.isArray(intent.deemphasis) ? intent.deemphasis : [] };
}

// `runLayoutWithIntent` (Tier-1 ELK + Tier-2 post-pass under a `LayoutIntent`)
// now lives in `layout-postpass.js` so the inline `MiniReactFlowPreview` card
// shares the EXACT same intent-honouring layout as this full renderer.

// ---- Inner component (must run inside <ReactFlowProvider>) ------------------

interface InnerProps extends ViewRendererProps {
  forwardedRef: ForwardedRef<ViewRendererHandle>;
}

function ViewRendererInner(props: InnerProps): JSX.Element {
  const {
    viewId,
    fetchLayout = true,
    layoutOverride,
    readOnly = false,
    onLayoutSave,
    theme = 'auto',
    apiContext,
    viewsApi = defaultViewsApi,
    enableZoom = false,
    requireZoomModifier = false,
    controlsPosition,
    reloadNonce,
    forwardedRef,
  } = props;

  const [state, setState] = useState<LoadState>(
    layoutOverride !== undefined
      ? {
          kind: 'ready',
          layout: layoutOverride,
          ...buildReactFlowGraph(layoutOverride),
        }
      : { kind: 'loading' },
  );
  const [dirty, setDirty] = useState(false);
  const [linearised, setLinearised] = useState(false);
  const initialPositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const rfInstanceRef = useRef<ReactFlowInstance | null>(null);
  const reactFlowApi = useReactFlow();
  // ELK uses nominal node dimensions (LEAF_W, computed leafHeight); the first
  // pass runs before React Flow has measured the actual DOM. When measurements
  // settle, edge anchors drift from ELK's port positions, producing visible
  // kinks and edges that appear to route through neighbouring nodes. The
  // post-measurement re-fit below re-runs ELK once `useNodesInitialized`
  // flips to true so edges anchor against the same coordinate frame React
  // Flow now renders in. Only fires when (a) the first pass actually ran ELK
  // and (b) the user hasn't started dragging yet.
  const firstPassFiredELKRef = useRef(false);
  const measuredRefitDoneRef = useRef(false);
  // One-shot latch for the auto-fit-on-open below: true once we've framed this
  // view, re-armed only when the view identity changes (see its reset effect).
  const autoFitDoneRef = useRef(false);
  const nodesInitialized = useNodesInitialized();
  // Phase 3 — "Show all" (clear scope) state. `scopeReloadNonce` bumps after a
  // successful clear to re-drive the fetch effect (which re-runs ELK on the now-
  // unscoped node set) without needing the parent to bump `reloadNonce`.
  const [scopeClearing, setScopeClearing] = useState(false);
  const [scopeReloadNonce, setScopeReloadNonce] = useState(0);

  // Fallback theme injection (no-op inside the AppShell).
  useEffect(() => {
    if (theme === 'light' || theme === 'dark') return;
    injectThemeCss();
  }, [theme]);

  // Layout fetch path.
  useEffect(() => {
    if (layoutOverride !== undefined) return;
    if (!fetchLayout) return;
    let cancelled = false;
    const ctx = apiContext ?? createApiContext();
    firstPassFiredELKRef.current = false;
    measuredRefitDoneRef.current = false;
    void (async () => {
      const result = await viewsApi.getLayout(ctx, viewId);
      if (cancelled) return;
      if (!result.ok) {
        setState({ kind: 'error', message: result.error.message });
        return;
      }
      const built = buildReactFlowGraph(result.value);
      const needsLayout = needsFirstRenderLayout(built.nodes);
      // ELK is the only place container dimensions are computed (parent
      // width/height = children bbox + padding). Without ELK the React Flow
      // wrapper has no explicit size and `ContainerBody`'s 100%/100% body
      // collapses to just the header, making parent blocks visually vanish
      // and breaking the next drag. So we run ELK whenever the layout has
      // any container node — even when positions are persisted — and only
      // discard ELK's positions in favour of the user's persisted ones.
      const hasContainerNode = built.nodes.some(
        (n) => typeof (n as { parentId?: string }).parentId === 'string',
      );
      if (needsLayout || hasContainerNode) {
        try {
          const intent = layoutIntentOf(result.value);
          const laid = await runLayoutWithIntent(built.nodes, built.edges, intent);
          if (cancelled) return;
          let finalNodes = laid.nodes;
          if (!needsLayout) {
            // Persisted layout: keep ELK-computed dimensions + the Tier-2
            // post-pass, but restore the user's positions on top so a previous
            // Save Layout (manual drag) still wins.
            const persisted = result.value.positions ?? {};
            finalNodes = laid.nodes.map((n) => {
              const p = persisted[n.id];
              return p ? { ...n, position: { x: p.x, y: p.y } } : n;
            });
          }
          setState({ kind: 'ready', layout: result.value, nodes: finalNodes, edges: laid.edges });
          initialPositionsRef.current = nodesToLayoutPositions(finalNodes);
          firstPassFiredELKRef.current = true;
        } catch {
          // ELK failure → render without auto-layout (positions stay at origin).
          setState({ kind: 'ready', layout: result.value, nodes: built.nodes, edges: built.edges });
          initialPositionsRef.current = nodesToLayoutPositions(built.nodes);
        }
      } else {
        setState({ kind: 'ready', layout: result.value, nodes: built.nodes, edges: built.edges });
        initialPositionsRef.current = nodesToLayoutPositions(built.nodes);
      }
    })();
    return (): void => {
      cancelled = true;
    };
    // `reloadNonce` participates so an in-place relayout (which changes the
    // persisted layout.json under the SAME viewId) forces a fresh fetch +
    // ELK re-run. Every other dep is unchanged from the original fetch effect.
  }, [viewId, fetchLayout, layoutOverride, apiContext, viewsApi, reloadNonce, scopeReloadNonce]);

  // Phase 3 — "Show all": clear the persisted scope, then re-fetch (bumping the
  // local nonce) so the full node set re-lays-out. Best-effort; a failed clear
  // leaves the scoped view + logs (no user-facing throw). Guarded re-entry.
  const handleClearScope = useCallback(async (): Promise<void> => {
    if (scopeClearing) return;
    if (viewsApi.clearScope === undefined) return;
    setScopeClearing(true);
    try {
      const ctx = apiContext ?? createApiContext();
      const res = await viewsApi.clearScope(ctx, viewId);
      if (res.ok) setScopeReloadNonce((n) => n + 1);
    } finally {
      setScopeClearing(false);
    }
  }, [scopeClearing, viewsApi, apiContext, viewId]);

  // Post-measurement re-fit: see firstPassFiredELKRef comment above.
  useEffect(() => {
    if (!nodesInitialized) return;
    if (!firstPassFiredELKRef.current) return;
    if (measuredRefitDoneRef.current) return;
    if (state.kind !== 'ready') return;
    if (dirty) return;
    measuredRefitDoneRef.current = true;
    let cancelled = false;
    const currentNodes = state.nodes;
    const currentEdges = state.edges;
    const intent = layoutIntentOf(state.layout);
    void (async () => {
      try {
        const laid = await runLayoutWithIntent(currentNodes, currentEdges, intent);
        if (cancelled) return;
        setState((prev) =>
          prev.kind === 'ready'
            ? { ...prev, nodes: laid.nodes, edges: laid.edges }
            : prev,
        );
        initialPositionsRef.current = nodesToLayoutPositions(laid.nodes);
      } catch {
        // Keep first-pass layout on failure.
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [nodesInitialized, state, dirty]);

  // Re-arm the auto-fit-on-open latch whenever the view identity changes (a
  // fresh fetch by `viewId`, or a new `layoutOverride`), so the next view
  // frames itself once. Kept separate from the fit effect so the latch reset
  // does not itself depend on `state`/`dirty`.
  useEffect(() => {
    autoFitDoneRef.current = false;
  }, [viewId, layoutOverride, reloadNonce]);

  // Auto-fit ONCE on open — zoom-enabled hosts (the expand-modal lightbox
  // `<ViewImageModal>` and the Views-tab `<ViewRendererPanel>`) must open
  // framed on the WHOLE diagram, zoomed to fit rather than zoomed in.
  //
  // React Flow's `fitView` *prop* fits only on a mount's first paint. For a
  // container / IBD view that first paint is the pre-ELK, origin-stacked layout
  // (children all at (0,0) with no measured size), so the prop-driven fit lands
  // fully zoomed in on that tiny origin cluster. When the async ELK pass then
  // spreads the nodes into their real positions the viewport is NEVER re-fit,
  // leaving the user staring at empty canvas until they manually zoom out —
  // exactly the reported symptom. We imperatively fit the first time a settled
  // (non-degenerate) layout lands, then latch (`autoFitDoneRef`) so we do it
  // exactly once per view. Latching matters: a later drag-then-save flips
  // `dirty` true→false, and re-fitting on that transition would yank the
  // viewport out from under the user mid-edit. `maxZoom: 1` stops small
  // diagrams over-zooming — they render at natural size, centred, not blown up.
  useEffect(() => {
    if (!enableZoom) return;
    if (linearised) return;
    if (state.kind !== 'ready') return;
    if (dirty) return;
    if (autoFitDoneRef.current) return;
    // Skip the pre-ELK origin-stacked frame so we never flash zoomed-in.
    if (needsFirstRenderLayout(state.nodes)) return;
    // NOTE: intentionally NOT gated on `useNodesInitialized()`. For a nested
    // container graph (an IBD with subsystem parents) that flag can stay
    // `false` indefinitely — a parent whose measured DOM height never resolves
    // keeps the whole graph "uninitialised" — which would suppress the fit
    // entirely (the exact failure we observed: viewport stuck zoomed-in while
    // React Flow's own fit-view control still framed correctly). `fitView`
    // derives its bounds from the ELK-assigned node width/height, so it frames
    // correctly without waiting on DOM measurement.
    //
    // The latch is set INSIDE `fit`, not before scheduling: React StrictMode
    // double-invokes effects (setup → cleanup → setup), and the cleanup cancels
    // the pending frame. Latching before scheduling would let the second setup
    // early-return (latch already true) after its predecessor's frame was
    // cancelled — the fit would never run. Setting it in the callback means the
    // last surviving frame both fits and latches exactly once.
    const fit = (): void => {
      autoFitDoneRef.current = true;
      void reactFlowApi.fitView({ padding: 0.15, maxZoom: 1 });
    };
    // Defer one frame so React Flow has committed the freshly-laid nodes into
    // its store before we read their bounding box.
    if (typeof requestAnimationFrame !== 'function') {
      fit();
      return;
    }
    const raf = requestAnimationFrame(fit);
    return (): void => cancelAnimationFrame(raf);
  }, [enableZoom, linearised, state, dirty, reactFlowApi]);

  // Capture initial positions when an override is supplied directly.
  //
  // Mirror the fetch-path ELK pass (see the `Layout fetch path` effect above):
  // when the layout has container nodes (any node with a `parentId`) the
  // ELK run is what computes container dimensions and child positions. The
  // override path previously skipped this, so the lightbox modal opened with
  // every child stacked at (0,0) inside its parent and the fixed
  // `defaultViewport` framing landed on whichever child happened to render
  // first — visually presenting "wrong block, wrong ports". Run ELK here
  // too so the modal renders the same graph the chat preview did.
  useEffect(() => {
    if (layoutOverride === undefined) return;
    let cancelled = false;
    const built = buildReactFlowGraph(layoutOverride);
    const hasContainerNode = built.nodes.some(
      (n) => typeof (n as { parentId?: string }).parentId === 'string',
    );
    const needsLayout = needsFirstRenderLayout(built.nodes);
    if (needsLayout || hasContainerNode) {
      // First-pass synchronous state so the ReactFlow canvas mounts
      // immediately; ELK then refines positions/dimensions asynchronously.
      setState({ kind: 'ready', layout: layoutOverride, nodes: built.nodes, edges: built.edges });
      initialPositionsRef.current = nodesToLayoutPositions(built.nodes);
      setDirty(false);
      firstPassFiredELKRef.current = false;
      measuredRefitDoneRef.current = false;
      const intent = layoutIntentOf(layoutOverride);
      void (async () => {
        try {
          const laid = await runLayoutWithIntent(built.nodes, built.edges, intent);
          if (cancelled) return;
          let finalNodes = laid.nodes;
          if (!needsLayout) {
            const persisted = layoutOverride.positions ?? {};
            finalNodes = laid.nodes.map((n) => {
              const p = persisted[n.id];
              return p ? { ...n, position: { x: p.x, y: p.y } } : n;
            });
          }
          setState({ kind: 'ready', layout: layoutOverride, nodes: finalNodes, edges: laid.edges });
          initialPositionsRef.current = nodesToLayoutPositions(finalNodes);
          firstPassFiredELKRef.current = true;
        } catch {
          // Keep first-pass layout on failure.
        }
      })();
    } else {
      initialPositionsRef.current = nodesToLayoutPositions(built.nodes);
      setState({ kind: 'ready', layout: layoutOverride, nodes: built.nodes, edges: built.edges });
      setDirty(false);
    }
    return () => {
      cancelled = true;
    };
  }, [layoutOverride]);

  // Dirty tracking — diff current node positions against initial snapshot.
  const checkDirty = useCallback((): boolean => {
    if (state.kind !== 'ready') return false;
    const initial = initialPositionsRef.current;
    for (const n of state.nodes) {
      const init = initial[n.id];
      if (!init) return true;
      if (init.x !== n.position.x || init.y !== n.position.y) return true;
    }
    return false;
  }, [state]);

  const buildLayoutSnapshot = useCallback((): ReactFlowLayoutJson => {
    if (state.kind !== 'ready') {
      return {
        version: '1',
        nodes: [],
        edges: [],
      };
    }
    const positions = nodesToLayoutPositions(state.nodes);
    // Overlay runtime edge endpoints (mutated by `onReconnect`) onto the
    // persisted layout edges so reconnected edges round-trip through save.
    // The layout-edge metadata (data.kind, etc.) is preserved; only
    // source/target/sourceHandle/targetHandle are taken from the runtime.
    const runtimeById = new Map<string, Edge>();
    for (const e of state.edges) runtimeById.set(e.id, e);
    const edges = state.layout.edges.map((laid) => {
      const rt = runtimeById.get(laid.id);
      if (!rt) return laid;
      const merged = { ...laid, source: rt.source, target: rt.target };
      if (rt.sourceHandle !== undefined && rt.sourceHandle !== null) {
        merged.sourceHandle = rt.sourceHandle;
      } else {
        delete merged.sourceHandle;
      }
      if (rt.targetHandle !== undefined && rt.targetHandle !== null) {
        merged.targetHandle = rt.targetHandle;
      } else {
        delete merged.targetHandle;
      }
      // Persist user-positioned waypoints. The runtime decorator stuffs
      // `data.onWaypointChange` + `data.readOnly` alongside `data.waypoints`;
      // strip those decorator fields out before writing to disk so the
      // saved layout only carries serialisable data.
      const rtData = (rt.data ?? {}) as Record<string, unknown>;
      const rtWaypointsRaw = rtData.waypoints;
      if (
        Array.isArray(rtWaypointsRaw) &&
        rtWaypointsRaw.length > 0 &&
        rtWaypointsRaw.every(
          (p) =>
            p !== null &&
            typeof p === 'object' &&
            typeof (p as { x?: unknown }).x === 'number' &&
            typeof (p as { y?: unknown }).y === 'number',
        )
      ) {
        merged.waypoints = (rtWaypointsRaw as Array<{ x: number; y: number }>).map((p) => ({
          x: p.x,
          y: p.y,
        }));
      } else {
        delete merged.waypoints;
      }
      return merged;
    });
    return {
      ...state.layout,
      nodes: state.layout.nodes,
      edges,
      positions,
      // DLA back-compat: upgrade a legacy 8-field layoutIntent to the current
      // shape so the strict PUT /layout schema accepts a pre-DLA view.
      ...(state.layout.layoutIntent !== undefined
        ? { layoutIntent: normalizeIntentForSave(state.layout.layoutIntent) }
        : {}),
    };
  }, [state]);

  const resetLayout = useCallback((): void => {
    if (state.kind !== 'ready') return;
    const intent = layoutIntentOf(state.layout);
    void (async () => {
      try {
        const reduced = prefersReducedMotion();
        const laid = await runLayoutWithIntent(state.nodes, state.edges, intent);
        // The reduced-motion path is the "snap instantly" branch — React Flow
        // does not animate setNodes itself; the toggle is informational for
        // future Toolbar wiring (W4-S2). We still consult it so the test
        // harness can verify the branch fires.
        void reduced;
        setState((prev) =>
          prev.kind === 'ready'
            ? { ...prev, nodes: laid.nodes, edges: laid.edges }
            : prev,
        );
        initialPositionsRef.current = nodesToLayoutPositions(laid.nodes);
        setDirty(false);
      } catch {
        // No-op on ELK failure; keep the existing layout.
      }
    })();
  }, [state]);

  const save = useCallback((): void => {
    const snapshot = buildLayoutSnapshot();
    if (typeof onLayoutSave === 'function') onLayoutSave(snapshot);
    initialPositionsRef.current =
      state.kind === 'ready' ? nodesToLayoutPositions(state.nodes) : {};
    setDirty(false);
  }, [buildLayoutSnapshot, onLayoutSave, state]);

  const fitView = useCallback((): void => {
    if (rfInstanceRef.current) {
      void rfInstanceRef.current.fitView({ padding: 0.1 });
      return;
    }
    if (typeof reactFlowApi.fitView === 'function') {
      void reactFlowApi.fitView({ padding: 0.1 });
    }
  }, [reactFlowApi]);

  const zoomIn = useCallback((): void => {
    if (rfInstanceRef.current && typeof rfInstanceRef.current.zoomIn === 'function') {
      void rfInstanceRef.current.zoomIn();
      return;
    }
    if (typeof reactFlowApi.zoomIn === 'function') {
      void reactFlowApi.zoomIn();
    }
  }, [reactFlowApi]);

  const zoomOut = useCallback((): void => {
    if (rfInstanceRef.current && typeof rfInstanceRef.current.zoomOut === 'function') {
      void rfInstanceRef.current.zoomOut();
      return;
    }
    if (typeof reactFlowApi.zoomOut === 'function') {
      void reactFlowApi.zoomOut();
    }
  }, [reactFlowApi]);

  const getZoom = useCallback((): number => {
    if (rfInstanceRef.current && typeof rfInstanceRef.current.getZoom === 'function') {
      return rfInstanceRef.current.getZoom();
    }
    if (typeof reactFlowApi.getZoom === 'function') {
      return reactFlowApi.getZoom();
    }
    return 1;
  }, [reactFlowApi]);

  const getCounts = useCallback((): { nodes: number; edges: number } => {
    if (state.kind !== 'ready') return { nodes: 0, edges: 0 };
    return { nodes: state.nodes.length, edges: state.edges.length };
  }, [state]);

  useImperativeHandle(
    forwardedRef,
    (): ViewRendererHandle => ({
      getLayout: buildLayoutSnapshot,
      isDirty: () => dirty || checkDirty(),
      resetLayout,
      save,
      fitView,
      zoomIn,
      zoomOut,
      getZoom,
      getCounts,
    }),
    [
      buildLayoutSnapshot,
      checkDirty,
      dirty,
      fitView,
      getCounts,
      getZoom,
      resetLayout,
      save,
      zoomIn,
      zoomOut,
    ],
  );

  // Edge reconnect — xyflow fires this when the user drags an edge endpoint
  // onto a different node/handle. We update runtime state.edges via the
  // `reconnectEdge` utility; buildLayoutSnapshot maps the change back into
  // the persisted layout edges on the next save.
  const onReconnect = useCallback(
    (oldEdge: Edge, newConnection: Connection): void => {
      setState((prev) => {
        if (prev.kind !== 'ready') return prev;
        const nextEdges = reconnectEdge(oldEdge, newConnection, prev.edges);
        return { ...prev, edges: nextEdges };
      });
      setDirty(true);
    },
    [],
  );

  // Edge waypoint drag — receives the full new waypoint list (empty array
  // clears all bends). Mirrors runReconnect's pattern: update runtime edges,
  // mark dirty; buildLayoutSnapshot copies the runtime list back into the
  // persisted layout edges on save.
  const handleEdgeWaypointChange = useCallback(
    (edgeId: string, waypoints: ReadonlyArray<{ x: number; y: number }>): void => {
      setState((prev) => {
        if (prev.kind !== 'ready') return prev;
        let mutated = false;
        const nextEdges = prev.edges.map((e) => {
          if (e.id !== edgeId) return e;
          mutated = true;
          const prevData = (e.data ?? {}) as Record<string, unknown>;
          const nextData: Record<string, unknown> = {
            ...prevData,
            waypoints: waypoints.map((p) => ({ x: p.x, y: p.y })),
          };
          return { ...e, data: nextData };
        });
        if (!mutated) return prev;
        return { ...prev, edges: nextEdges };
      });
      setDirty(true);
    },
    [],
  );

  const onNodesChange = useCallback(
    (changes: ReadonlyArray<{ type: string; id?: string }>): void => {
      // We do not own the React Flow uncontrolled-state machinery; instead we
      // mirror the changes back into our local nodes state. The minimal
      // implementation only tracks position-change events for the dirty flag.
      setState((prev) => {
        if (prev.kind !== 'ready') return prev;
        let next = prev.nodes;
        let mutated = false;
        for (const change of changes) {
          const ch = change as {
            type: string;
            id?: string;
            position?: { x: number; y: number };
          };
          if (ch.type === 'position' && ch.id !== undefined && ch.position) {
            const idx = next.findIndex((n) => n.id === ch.id);
            if (idx >= 0) {
              if (!mutated) {
                next = [...next];
                mutated = true;
              }
              next[idx] = { ...next[idx]!, position: ch.position };
            }
          }
        }
        if (!mutated) return prev;
        return { ...prev, nodes: next };
      });
      // Mark dirty whenever a position change lands.
      const hasPositionChange = changes.some((c) => c.type === 'position');
      if (hasPositionChange) setDirty(true);
    },
    [],
  );

  const rootStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    minHeight: 320,
  };
  const dataTheme: 'light' | 'dark' | undefined =
    theme === 'light' ? 'light' : theme === 'dark' ? 'dark' : undefined;

  if (state.kind === 'loading') {
    return (
      <div
        className="tessa-view-renderer"
        data-state="loading"
        data-theme={dataTheme}
        role="application"
        aria-label={`View renderer for ${viewId}`}
        style={rootStyle}
      >
        <div className="view-renderer-loading">Loading view…</div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div
        className="tessa-view-renderer"
        data-state="error"
        data-theme={dataTheme}
        role="application"
        aria-label={`View renderer for ${viewId}`}
        style={rootStyle}
      >
        <div className="view-renderer-error" role="alert">
          Failed to render view. Re-run / Open source <code>.sysml</code>.
          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--tessa-muted)' }}>
            {state.message}
          </div>
        </div>
      </div>
    );
  }

  const counts = { nodes: state.nodes.length, edges: state.edges.length };
  const summaryId = `view-${viewId}-summary`;
  const virtualise = state.nodes.length > VIRTUALISE_THRESHOLD;
  // Phase 3 — how many parts the current scope hides (0 when unscoped). Drives
  // the faithfulness badge overlay so the Views-tab editor can't be mistaken for
  // the complete model either.
  const hiddenCount = resolveHiddenNodeIds(
    state.layout,
    state.layout.viewScope ?? defaultViewScope(),
  ).size;

  // Decorate every runtime edge with the waypoint callback + readOnly flag
  // so ConnectionEdge can render and dispatch drag updates without owning a
  // store reference. The decorator only mutates `data`; `id/source/target`
  // and ELK fields stay intact.
  const decoratedEdges: Edge[] = state.edges.map((e) => {
    const prevData = (e.data ?? {}) as Record<string, unknown>;
    return {
      ...e,
      data: {
        ...prevData,
        onWaypointChange: handleEdgeWaypointChange,
        readOnly,
      },
    };
  });

  return (
    <div
      className="tessa-view-renderer"
      data-state="ready"
      data-theme={dataTheme}
      data-dirty={dirty ? 'true' : 'false'}
      role="application"
      aria-label={`View renderer for ${viewId}`}
      aria-describedby={summaryId}
      style={rootStyle}
    >
      <span id={summaryId} className="view-renderer-summary">
        {counts.nodes} nodes, {counts.edges} edges
      </span>
      <button
        type="button"
        className="linearised-toggle"
        aria-pressed={linearised}
        onClick={(): void => setLinearised((v) => !v)}
      >
        {linearised ? 'Show diagram' : 'Show linearised view'}
      </button>
      {linearised ? (
        <ul className="linearised-list" data-linearised="true">
          {state.nodes.map((n) => {
            const inbound = state.edges.filter((e) => e.target === n.id);
            const outbound = state.edges.filter((e) => e.source === n.id);
            const data = (n.data ?? {}) as { label?: string; name?: string };
            const label = data.label ?? data.name ?? n.id;
            return (
              <li key={n.id} data-node-id={n.id}>
                <strong>{label}</strong>
                <span style={{ color: 'var(--tessa-muted)' }}>
                  {' '}
                  — in: {inbound.length}, out: {outbound.length}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <>
          <ReactFlow
            nodes={state.nodes}
            edges={decoratedEdges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            // `enableZoom` is flipped on by the lightbox-modal callers
            // (`<ViewImageModal>`), and there the whole graph must be visible
            // on first paint. Use `fitView` in that mode so the modal frames
            // the entire layout instead of the previous fixed top-left corner
            // (`defaultViewport={x:40,y:40,zoom:1}`), which mis-framed multi-
            // node container layouts and made the wrong block look canonical.
            {...(enableZoom
              ? { fitView: true, fitViewOptions: { padding: 0.15 } }
              : { defaultViewport: { x: 40, y: 40, zoom: 1 } })}
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{ type: 'connection' }}
            nodesDraggable={!readOnly}
            nodesConnectable={false}
            edgesReconnectable={!readOnly}
            onReconnect={onReconnect}
            elementsSelectable={true}
            panOnDrag={enableZoom}
            panOnScroll={enableZoom}
            zoomOnScroll={enableZoom}
            zoomOnPinch={enableZoom}
            zoomOnDoubleClick={enableZoom && !requireZoomModifier}
            zoomActivationKeyCode={
              enableZoom && requireZoomModifier ? 'Control' : null
            }
            panActivationKeyCode={
              enableZoom && requireZoomModifier ? 'Shift' : null
            }
            minZoom={enableZoom ? 0.1 : undefined}
            maxZoom={enableZoom ? 4 : undefined}
            preventScrolling={enableZoom}
            onlyRenderVisibleElements={virtualise}
            onInit={(instance): void => {
              rfInstanceRef.current = instance;
            }}
            onNodesChange={onNodesChange}
          >
            <Background gap={18} size={1} color="var(--tessa-canvas-dot)" />
            {enableZoom ? (
              <Controls
                showInteractive={false}
                {...(controlsPosition !== undefined ? { position: controlsPosition } : {})}
              />
            ) : null}
          </ReactFlow>
          <EdgeLegend edges={state.edges} position="bottom-right" />
          {hiddenCount > 0 ? (
            <div
              data-testid="view-renderer-scope-badge"
              role="status"
              style={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '2px 8px',
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                color: '#78350f',
                background: '#fffbeb',
                border: '1px solid #f59e0b',
              }}
            >
              <span>
                {hiddenCount} {hiddenCount === 1 ? 'part' : 'parts'} hidden — partial view
              </span>
              {viewsApi.clearScope !== undefined && !readOnly ? (
                <button
                  type="button"
                  data-testid="view-renderer-scope-show-all"
                  aria-label="Show all parts"
                  onClick={() => void handleClearScope()}
                  disabled={scopeClearing}
                  style={{
                    padding: '1px 6px',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    cursor: scopeClearing ? 'not-allowed' : 'pointer',
                    color: 'inherit',
                    background: 'transparent',
                    border: '1px solid #b45309',
                  }}
                >
                  {scopeClearing ? 'Showing…' : 'Show all'}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ---- Public component -------------------------------------------------------

export const ViewRenderer = forwardRef<ViewRendererHandle, ViewRendererProps>(
  function ViewRenderer(props, ref): JSX.Element {
    return (
      <ReactFlowProvider>
        <ViewRendererInner {...props} forwardedRef={ref} />
      </ReactFlowProvider>
    );
  },
);

export default ViewRenderer;
